import { useMemo } from 'react';
import { useQueries, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { CommandParameterDescriptor, CommandType } from '@thingslog/repositories';
import { QueryKeys } from '@thingslog/queries';
import { commandsQueryClient } from '../../../../clients/ReactQueryClients/ReactQueryClients';
import {
  collectParameterValueLabels,
  collectSharedParameterValueLabels,
  ParameterValueLabels,
  SharedParameterValueLabels,
} from './commandLabels';

export interface CommandParameterValueLabels {
  labels: ParameterValueLabels;
  shared: SharedParameterValueLabels;
}

// Retries off: some types answer a permanent 404, and cells fall back to the
// raw value anyway.
const useCommandParameterValueLabels = (
  deviceNumber: string,
  commandTypes: CommandType[]
): CommandParameterValueLabels => {
  const distinctTypes = useMemo(
    (): CommandType[] => [...new Set(commandTypes)].sort(),
    [commandTypes.join('|')]
  );

  const results = useQueries({
    queries: distinctTypes.map((commandType: CommandType) => ({
      queryKey: [QueryKeys.GetCommandTypeParameters, commandType, deviceNumber],
      queryFn: (): Promise<CommandParameterDescriptor[]> =>
        commandsQueryClient.getCommandTypeParameters(commandType, deviceNumber),
      enabled: Boolean(deviceNumber),
      retry: false,
      staleTime: Infinity,
    })),
  });

  const readyCount = results.filter(
    (result: UseQueryResult<CommandParameterDescriptor[], AxiosError>): boolean =>
      result.data !== undefined
  ).length;

  return useMemo((): CommandParameterValueLabels => {
    const labels: ParameterValueLabels = new Map<string, string>();
    const loaded: [CommandType, CommandParameterDescriptor[]][] = [];

    distinctTypes.forEach((commandType: CommandType, index: number): void => {
      const descriptors = results[index]?.data;
      if (!descriptors) return;

      loaded.push([commandType, descriptors]);
      collectParameterValueLabels(commandType, descriptors).forEach(
        (displayName: string, key: string): void => {
          labels.set(key, displayName);
        }
      );
    });

    return { labels, shared: collectSharedParameterValueLabels(loaded) };
  }, [distinctTypes, readyCount]);
};

export default useCommandParameterValueLabels;
